// client/src/components/people/PeopleTable.js
// The people table page, in two views over the same contacts:
//   individuals — every person: all roles, owner and days-since filters, paging
//   partners    — RGA Partners only (from /api/partners, which adds
//                 active_mandates): agreement, licensed and mandate columns
//                 and filters, no paging
// Both get checkbox selection, merge, bulk delete, export, the PersonModal
// and PersonForm. Clicking anywhere on a row opens the modal; only the
// checkbox and the trash icon have their own action.
import { useState, useMemo, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as XLSX from 'xlsx'
import api from '../../lib/api'
import { useIsPreview } from '../../lib/preview'
import { RGA_PARTNER, useRoleOptions } from '../../lib/roles'
import { firmNames, FirmLines } from '../../lib/contactFirms'
import { PL_BADGE } from '../../lib/pipelineStages'
import MergeModal from '../MergeModal'
import PersonForm from './PersonForm'
import PersonModal from './PersonModal'
import { CONTACT_TYPE_BADGE, invalidatePeople, tagColor, formatDate, todayStr, Modal, TrashIcon, DeleteConfirmModal, SortHeader, MultiSelectFilter } from './ui'

// ── Constants ─────────────────────────────────────────────────────────────

const DSC_RANGES = [
  { label: '< 7 days',    test: d => d != null && d < 7 },
  { label: '7-30 days',   test: d => d != null && d >= 7 && d <= 30 },
  { label: '30-60 days',  test: d => d != null && d > 30 && d <= 60 },
  { label: '60+ days',    test: d => d != null && d > 60 },
]

const SORTABLE_COLS = {
  name:      { label: 'Name', fn: (a, b) => (a.name || '').localeCompare(b.name || '') },
  firm:      { label: 'Firm', fn: (a, b) => firmNames(a).localeCompare(firmNames(b)) },
  title:     { label: 'Role / Title', fn: (a, b) => (a.title_role || '').localeCompare(b.title_role || '') },
  email:     { label: 'Email', fn: (a, b) => ((a.email ?? [])[0] || '').localeCompare((b.email ?? [])[0] || '') },
  last_interaction: { label: 'Last Interaction', fn: (a, b) => (a.last_interaction || '').localeCompare(b.last_interaction || '') },
  days_since: { label: 'Days Since', fn: (a, b) => (a.days_since_contact ?? 9999) - (b.days_since_contact ?? 9999) },
  agreement: { label: 'Partnership Agreement', fn: (a, b) => (a.partnership_agreement ? 1 : 0) - (b.partnership_agreement ? 1 : 0) },
  licensed:  { label: 'Licensed', fn: (a, b) => (a.is_licensed ? 1 : 0) - (b.is_licensed ? 1 : 0) },
  involvement: { label: 'Mandates', fn: (a, b) => mandateInvolvement(a).length - mandateInvolvement(b).length },
  mandates:  { label: 'Active Mandates', fn: (a, b) => (a.active_mandates?.length ?? 0) - (b.active_mandates?.length ?? 0) },
  notes:     { label: 'Notes', fn: (a, b) => (a.notes || '').localeCompare(b.notes || '') },
}

const muted = <span style={{ color: 'var(--text-muted)' }}>—</span>

// Each mandate a person is involved in as a name on its pipelines: the
// stage once they're on it, otherwise the partner submission's state (With
// RGA, Not approved). Mandates they have nothing to do with don't appear.
// Partners' own mandates are in the RGA Partners view, not here.
const SUBMISSION_STATUS = {
  pending:  { label: 'With RGA',  badge: 'badge-yellow' },
  rejected: { label: 'Not approved', badge: 'badge-gray' },
}
function mandateInvolvement(c) {
  const rows = new Map()
  const add = (mandate, role, entry) => {
    const key = `${mandate.mandate_id}:${role}`
    if (!rows.has(key)) rows.set(key, { key, ...mandate, ...entry })
  }
  for (const r of [...(c.pipeline ?? []), ...(c.name_pipeline ?? [])]) {
    if (r.mandate) add(r.mandate, 'name', { label: r.stage, badge: PL_BADGE[r.stage] ?? 'badge-gray', title: `On the pipeline: ${r.stage}` })
  }
  // Pending before rejected, so a resubmission waiting on RGA shows.
  const subs = [...(c.submissions ?? [])].sort((a, b) => (a.status === 'pending' ? -1 : 0) - (b.status === 'pending' ? -1 : 0))
  for (const x of subs) {
    const st = SUBMISSION_STATUS[x.status]
    if (st && x.mandate) add(x.mandate, 'name', { ...st, title: `Submitted by ${x.partner?.name ?? 'a partner'}` })
  }
  return [...rows.values()].sort((a, b) => a.mandate_name.localeCompare(b.mandate_name))
}
const stack = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }

// ── Columns ───────────────────────────────────────────────────────────────
// Each: header, optional sort key, td style, and the cell (plain text only).

const COLUMNS = {
  name: { header: 'Name', sort: 'name', style: { fontWeight: 500 }, cell: c => c.name },
  firm: { header: 'Firm', sort: 'firm', style: { color: 'var(--text-secondary)', fontSize: 12 },
    cell: c => <FirmLines contact={c} /> },
  title: { header: 'Role / Title', sort: 'title', style: { color: 'var(--text-secondary)', fontSize: 12 },
    cell: c => c.title_role ?? '—' },
  email: { header: 'Email', sort: 'email', style: { fontSize: 12 },
    cell: c => (c.email ?? []).length === 0
      ? muted
      : c.email.map(e => (
          <div key={e}
            style={{ color: 'var(--text-secondary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 180 }}
            title={e}>
            {e}
          </div>
        )) },
  type: { header: 'Type',
    cell: c => (
      <div style={stack}>
        {(c.contact_type ?? []).map(t => (
          <span key={t} className={`badge ${CONTACT_TYPE_BADGE[t] ?? 'badge-gray'}`}
            style={{ fontSize: 10 }}>{t}</span>
        ))}
      </div>
    ) },
  agreement: { header: 'Partnership Agreement', sort: 'agreement',
    cell: c => (
      <span className={`badge ${c.partnership_agreement ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: 11 }}>
        {c.partnership_agreement ? 'Yes' : 'No'}
      </span>
    ) },
  licensed: { header: 'Licensed', sort: 'licensed',
    cell: c => (
      <span className={`badge ${c.is_licensed ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: 11 }}>
        {c.is_licensed ? 'Yes' : 'No'}
      </span>
    ) },
  mandates: { header: 'Active Mandates', sort: 'mandates',
    cell: c => (
      <div style={stack}>
        {(c.active_mandates ?? []).length === 0
          ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
          : c.active_mandates.map(m => (
              <span key={m.mandate_id} className="badge badge-blue" style={{ fontSize: 10 }}>
                {m.mandate_name}
              </span>
            ))
        }
      </div>
    ) },
  // Only the mandates the person is involved in; empty for everyone else.
  involvement: { header: 'Mandates', sort: 'involvement', style: { fontSize: 12 },
    cell: c => {
      const rows = mandateInvolvement(c)
      if (!rows.length) return muted
      return (
        <div style={stack}>
          {rows.map(r => (
            <div key={r.key} style={{ whiteSpace: 'nowrap' }} title={r.title}>
              <span style={{ color: 'var(--text-primary)' }}>{r.mandate_name}</span>{' '}
              <span className={`badge ${r.badge}`} style={{ fontSize: 10 }}>{r.label}</span>
            </div>
          ))}
        </div>
      )
    } },
  tags: { header: 'Tags',
    cell: c => (
      <div style={stack}>
        {(c.tags ?? []).map(t => (
          <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 10 }}>{t}</span>
        ))}
      </div>
    ) },
  last_interaction: { header: 'Last Interaction', sort: 'last_interaction',
    style: { fontSize: 12, color: 'var(--text-secondary)' },
    cell: c => formatDate(c.last_interaction) ?? '—' },
  days_since: { header: 'Days Since', sort: 'days_since',
    cell: c => {
      const dsc = c.days_since_contact
      return dsc != null
        ? <span style={{
            fontSize: 12, fontWeight: dsc > 30 ? 600 : 400,
            color: dsc > 30 ? 'var(--danger)' : 'var(--text-primary)',
          }}>{dsc}d</span>
        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
    } },
  owner: { header: 'Owner', style: { fontSize: 12, color: 'var(--text-secondary)' },
    cell: (c, ctx) => ctx.ownerName(c) },
  notes: { header: 'Notes', sort: 'notes', style: { fontSize: 12, color: 'var(--text-secondary)', maxWidth: 180 },
    cell: c => c.notes
      ? <span title={c.notes}>{c.notes.length > 50 ? c.notes.slice(0, 50) + '...' : c.notes}</span>
      : muted },
}

// ── Export helpers ────────────────────────────────────────────────────────

function writeSheet(data, sheet, filePrefix, format) {
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheet)
  const filename = `${filePrefix}_${todayStr()}.${format === 'xlsx' ? 'xlsx' : 'csv'}`
  if (format === 'xlsx') {
    XLSX.writeFile(wb, filename)
  } else {
    XLSX.writeFile(wb, filename, { bookType: 'csv' })
  }
}

function exportContacts(rows, users, format) {
  const resolveOwners = (ids) => {
    if (!ids || !ids.length) return ''
    return ids.map(id => {
      const u = users.find(u => u.id === id)
      return u ? u.name : null
    }).filter(Boolean).join(', ')
  }

  writeSheet(rows.map(c => ({
    'Name':               c.name ?? '',
    'Title/Role':         c.title_role ?? '',
    'Firms':              firmNames(c),
    'Email(s)':           (c.email ?? []).join(', '),
    'Phone(s)':           (c.phone ?? []).join(', '),
    'Contact Type':       (c.contact_type ?? []).join(', '),
    'Tags':               (c.tags ?? []).join(', '),
    'Introduced By':      '',
    'Relationship Owner': resolveOwners(c.relationship_owner ?? []),
    'Last Interaction':   c.last_interaction ? new Date(c.last_interaction).toLocaleDateString() : '',
    'Days Since Contact': c.days_since_contact ?? '',
  })), 'Contacts', 'RGA_Contacts', format)
}

function exportPartners(rows, users, format) {
  writeSheet(rows.map(p => ({
    'Name':                  p.name ?? '',
    'Firm':                  firmNames(p),
    'Partnership Agreement': p.partnership_agreement ? 'Yes' : 'No',
    'Licensed':              p.is_licensed ? 'Yes' : 'No',
    'Active Mandates':       (p.active_mandates ?? []).map(m => m.mandate_name).join(', '),
    'Tags':                  (p.tags ?? []).join(', '),
    'Notes':                 p.notes ?? '',
  })), 'Partners', 'RGA_Partners', format)
}

// ── Views ─────────────────────────────────────────────────────────────────

const VIEWS = {
  individuals: {
    title: 'Individuals',
    count: n => `${n} individual${n !== 1 ? 's' : ''}`,
    queryKey: ['contacts'], url: '/api/contacts',
    columns: ['name', 'firm', 'title', 'email', 'type', 'involvement', 'tags', 'last_interaction', 'days_since', 'owner', 'notes'],
    paged: true,
    exportRows: exportContacts,
    createLabel: '+ New Individual', createTitle: 'New Individual', defaultTypes: [],
    noun: 'Contact',
    empty: { filtered: 'No individuals match your filters', none: 'No individuals yet', hint: 'Add your first individual to get started.' },
  },
  partners: {
    title: 'RGA Partners',
    count: n => `${n} RGA partner${n !== 1 ? 's' : ''}`,
    subtitle: 'Partner advisors and their engagement status',
    queryKey: ['partners'], url: '/api/partners',
    columns: ['name', 'firm', 'agreement', 'licensed', 'mandates', 'tags', 'notes'],
    paged: false,
    exportRows: exportPartners,
    createLabel: '+ New RGA Partner', createTitle: 'New RGA Partner', defaultTypes: [RGA_PARTNER],
    noun: 'Partner',
    empty: { filtered: 'No partners match your filters', none: 'No partners yet', hint: 'Create your first RGA partner to get started.' },
  },
}

// ── Pagination bar ────────────────────────────────────────────────────────

function PaginationBar({ page, totalPages, pageStart, pageSize, totalCount, onPage }) {
  const from = totalCount === 0 ? 0 : pageStart + 1
  const to   = Math.min(pageStart + pageSize, totalCount)

  function pageNums() {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages = []
    pages.push(1)
    if (page > 4) pages.push('…')
    const start = Math.max(2, page - 2)
    const end   = Math.min(totalPages - 1, page + 2)
    for (let i = start; i <= end; i++) pages.push(i)
    if (page < totalPages - 3) pages.push('…')
    pages.push(totalPages)
    return pages
  }

  const btnBase = {
    minWidth: 30, height: 28, padding: '0 7px', borderRadius: 4,
    border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        Showing {from}–{to} of {totalCount} records
      </span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <button
          onClick={() => onPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{ ...btnBase, background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
        >‹ Prev</button>

        {pageNums().map((n, i) =>
          n === '…'
            ? <span key={`e${i}`} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '0 4px' }}>…</span>
            : <button
                key={n}
                onClick={() => onPage(n)}
                style={{
                  ...btnBase,
                  background: n === page ? 'var(--accent)' : 'var(--surface-raised)',
                  color: n === page ? '#fff' : 'var(--text-secondary)',
                  fontWeight: n === page ? 700 : 400,
                  borderColor: n === page ? 'var(--accent)' : 'var(--border)',
                }}
              >{n}</button>
        )}

        <button
          onClick={() => onPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          style={{ ...btnBase, background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
        >Next ›</button>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function PeopleTable({ view: viewName, profileBase }) {
  const isPreview = useIsPreview()
  const view = VIEWS[viewName]
  const isPartners = viewName === 'partners'
  const { allContactTypes } = useRoleOptions()
  const [search, setSearch]             = useState('')
  const [typeFilter, setTypeFilter]     = useState([])
  const [tagFilter, setTagFilter]       = useState([])
  const [ownerFilter, setOwnerFilter]   = useState('')
  const [dscFilter, setDscFilter]       = useState('')
  const [agreementFilter, setAgreement] = useState('')
  const [licensedFilter, setLicensed]   = useState('')
  const [mandateFilter, setMandateFilter] = useState([])
  const [sortCol, setSortCol]           = useState('name')
  const [sortDir, setSortDir]           = useState('asc')
  const [page, setPage]                 = useState(1)
  const [pageSize, setPageSize]         = useState(20)
  const [selectedId, setSelectedId]     = useState(null)
  const [createOpen, setCreate]         = useState(false)
  const [toDelete, setToDelete]         = useState(null)
  // ── merge / bulk-delete state ────────────────────────────────────────────
  const [checkedIds, setCheckedIds]     = useState(new Set())
  const [mergeOpen, setMergeOpen]       = useState(false)
  const [mergePrimaryId, setMergePrimaryId] = useState(null)
  const [mergeError, setMergeError]     = useState(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting]     = useState(false)
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null) // { succeeded, failed }
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: rowsData, isLoading } = useQuery({
    queryKey: view.queryKey,
    queryFn: () => api.get(view.url).then(r => r.data.data ?? []),
  })
  // Every contact, for the form's Introduced By picker (the same list as the
  // rows on Individuals).
  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const { data: usersData } = useQuery({
    queryKey: ['relationship-owners'],
    queryFn: () => api.get('/api/users/relationship-owners').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })
  const { data: tagSuggestions } = useQuery({
    queryKey: ['contactTags'],
    queryFn: () => api.get('/api/contacts/tags').then(r => r.data.data ?? []),
    staleTime: 60 * 1000,
  })
  const { data: mandatesData } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
    staleTime: 60 * 1000,
    enabled: isPartners,
  })

  const rows     = useMemo(() => rowsData ?? [], [rowsData])
  const contacts = useMemo(() => contactsData ?? [], [contactsData])
  const firms    = firmsData ?? []
  const users    = useMemo(() => usersData ?? [], [usersData])
  // Looked up by id so the modal shows fresh data after an edit
  const selected = rows.find(c => c.contact_id === selectedId) ?? null

  // Deep link from other pages (e.g. a firm's contact list): ?open=<contact_id>
  // auto-opens that person's detail modal once its data has loaded. The
  // param is then dropped, so a later refetch doesn't reopen a closed modal.
  useEffect(() => {
    const openId = searchParams.get('open')
    if (!openId || !rows.length) return
    if (rows.some(c => c.contact_id === openId)) setSelectedId(openId)
    setSearchParams(p => { p.delete('open'); return p }, { replace: true })
  }, [searchParams, setSearchParams, rows])

  // Tag filter options: tags on the rows (plus every known tag on Partners)
  const allTags = useMemo(() => {
    const s = new Set(isPartners ? (tagSuggestions ?? []) : [])
    for (const c of rows) { for (const t of (c.tags ?? [])) s.add(t) }
    return Array.from(s).sort()
  }, [rows, tagSuggestions, isPartners])

  const allMandateNames = useMemo(
    () => (mandatesData ?? []).map(m => m.mandate_name).filter(Boolean).sort(),
    [mandatesData])

  const ownerName = useCallback((contact) => {
    const ids = Array.isArray(contact.relationship_owner) ? contact.relationship_owner : []
    if (!ids.length) return '—'
    const names = ids.map(id => {
      const u = users.find(u => u.id === id)
      return u ? u.name : id.slice(0, 8) + '...'
    })
    if (names.length <= 2) return names.join(', ')
    return names.slice(0, 2).join(', ') + ` +${names.length - 2} more`
  }, [users])

  const toggle = setter => v => setter(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])

  // Boolean() — without it, `... || 0 || 0` returns the number 0 which React renders as literal "0"
  const hasFilters = Boolean(search || typeFilter.length || tagFilter.length || ownerFilter || dscFilter
    || agreementFilter || licensedFilter || mandateFilter.length)
  const activeFilterCount = [typeFilter.length, tagFilter.length, ownerFilter, dscFilter,
    agreementFilter, licensedFilter, mandateFilter.length].filter(Boolean).length

  function clearAllFilters() {
    setSearch(''); setTypeFilter([]); setTagFilter([]); setOwnerFilter(''); setDscFilter('')
    setAgreement(''); setLicensed(''); setMandateFilter([])
    setPage(1)
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const filtered = useMemo(() => {
    let list = rows.filter(c => {
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false
      if (typeFilter.length && !typeFilter.some(t => c.contact_type?.includes(t))) return false
      if (tagFilter.length && !tagFilter.some(t => (c.tags ?? []).includes(t))) return false
      if (ownerFilter && !(c.relationship_owner ?? []).includes(ownerFilter)) return false
      if (dscFilter) {
        const range = DSC_RANGES.find(r => r.label === dscFilter)
        if (range && !range.test(c.days_since_contact)) return false
      }
      if (agreementFilter && (agreementFilter === 'yes') !== !!c.partnership_agreement) return false
      if (licensedFilter && (licensedFilter === 'yes') !== !!c.is_licensed) return false
      if (mandateFilter.length && !mandateFilter.some(m => (c.active_mandates ?? []).some(am => am.mandate_name === m))) return false
      return true
    })

    const sortFn = SORTABLE_COLS[sortCol]?.fn
    if (sortFn) {
      list = [...list].sort((a, b) => sortDir === 'asc' ? sortFn(a, b) : sortFn(b, a))
    }
    return list
  }, [rows, search, typeFilter, tagFilter, ownerFilter, dscFilter, agreementFilter, licensedFilter, mandateFilter, sortCol, sortDir])

  // Reset to page 1 whenever filters or sort change
  useEffect(() => { setPage(1) }, [search, typeFilter, tagFilter, ownerFilter, dscFilter, agreementFilter, licensedFilter, mandateFilter, sortCol, sortDir])

  const effPageSize = view.paged ? pageSize : Math.max(1, filtered.length)
  const totalPages = Math.max(1, Math.ceil(filtered.length / effPageSize))
  const safePage   = Math.min(page, totalPages)
  const pageStart  = (safePage - 1) * effPageSize
  const paginated  = filtered.slice(pageStart, pageStart + effPageSize)

  const createMutation = useMutation({
    mutationFn: payload => api.post('/api/contacts', payload).then(r => r.data),
    onSuccess: () => { invalidatePeople(qc); setCreate(false) },
  })

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/contacts/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      invalidatePeople(qc, id)
      setToDelete(null)
      setSelectedId(prev => prev === id ? null : prev)
    },
  })

  const mergeMutation = useMutation({
    mutationFn: ({ primary_id, merge_ids }) =>
      api.post('/api/contacts/merge', { primary_id, merge_ids }).then(r => r.data),
    onSuccess: () => {
      invalidatePeople(qc)
      setCheckedIds(new Set())
      setMergeOpen(false)
      setMergeError(null)
    },
    onError: err => setMergeError(err.response?.data?.error || err.message),
  })

  // ── checkbox helpers ─────────────────────────────────────────────────────
  function toggleCheck(id, e) {
    e.stopPropagation()
    setCheckedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function toggleCheckAll() {
    const pageIds = paginated.map(c => c.contact_id)
    const allPageChecked = pageIds.every(id => checkedIds.has(id))
    if (allPageChecked) {
      setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.delete(id)); return n })
    } else {
      setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.add(id)); return n })
    }
  }

  const checkedRecords = useMemo(
    () => rows.filter(c => checkedIds.has(c.contact_id)),
    [rows, checkedIds]
  )

  function openMerge() {
    const sorted = [...checkedRecords].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    )
    setMergePrimaryId(sorted[0]?.contact_id ?? null)
    setMergeError(null)
    setMergeOpen(true)
  }

  async function bulkDelete() {
    setBulkDeleting(true)
    setBulkDeleteResult(null)
    let succeeded = 0, failed = 0
    for (const id of checkedIds) {
      try {
        await api.delete(`/api/contacts/${id}`)
        succeeded++
      } catch {
        failed++
      }
    }
    invalidatePeople(qc)
    setCheckedIds(new Set())
    setBulkDeleting(false)
    setBulkDeleteOpen(false)
    if (failed > 0) setBulkDeleteResult({ succeeded, failed })
  }

  const cols = view.columns.map(k => COLUMNS[k])
  const ctx = { ownerName }
  const plural = n => `${view.noun}${n !== 1 ? 's' : ''}`

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>{view.title}</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>
              {isLoading ? 'Loading...' : view.count(rows.length)}
              {view.subtitle && !isPreview && <>{' '}<span style={{ color: 'var(--text-muted)' }}>— {view.subtitle}</span></>}
            </span>
            {checkedIds.size > 0 && (
              <button
                onClick={() => setCheckedIds(new Set())}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  fontSize: 12, color: 'var(--text-secondary)',
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                Clear {checkedIds.size} selected
              </button>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {checkedIds.size >= 2 && (
            <button className="btn btn-secondary" onClick={openMerge}
              style={{ fontWeight: 600 }}>
              Merge Selected ({checkedIds.size})
            </button>
          )}
          {checkedIds.size >= 1 && (
            <button className="btn btn-danger" onClick={() => { setBulkDeleteResult(null); setBulkDeleteOpen(true) }}>
              Delete Selected ({checkedIds.size})
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => view.exportRows(filtered, users, 'csv')}>Export CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => view.exportRows(filtered, users, 'xlsx')}>Export Excel</button>
          <button className="btn btn-primary" onClick={() => setCreate(true)}>{view.createLabel}</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by name..."
          value={search} onChange={e => setSearch(e.target.value)} />

        {isPartners ? <>
          <select className="select" style={{ maxWidth: 200, fontSize: 12 }} value={agreementFilter}
            onChange={e => setAgreement(e.target.value)}>
            <option value="">Partnership Agreement</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>

          <select className="select" style={{ maxWidth: 160, fontSize: 12 }} value={licensedFilter}
            onChange={e => setLicensed(e.target.value)}>
            <option value="">Licensed</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>

          <MultiSelectFilter label="Mandates" options={allMandateNames}
            selected={mandateFilter} onToggle={toggle(setMandateFilter)} />

          <MultiSelectFilter label="Tags" options={allTags}
            selected={tagFilter} onToggle={toggle(setTagFilter)} />
        </> : <>
          <MultiSelectFilter label="Contact Type" options={allContactTypes}
            selected={typeFilter} onToggle={toggle(setTypeFilter)} badgeMap={CONTACT_TYPE_BADGE} />

          {allTags.length > 0 && (
            <MultiSelectFilter label="Tags" options={allTags}
              selected={tagFilter} onToggle={toggle(setTagFilter)} />
          )}

          <select className="select" style={{ maxWidth: 180, fontSize: 12 }} value={ownerFilter}
            onChange={e => setOwnerFilter(e.target.value)}>
            <option value="">All owners</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>

          <select className="select" style={{ maxWidth: 160, fontSize: 12 }} value={dscFilter}
            onChange={e => setDscFilter(e.target.value)}>
            <option value="">Days since...</option>
            {DSC_RANGES.map(r => <option key={r.label} value={r.label}>{r.label}</option>)}
          </select>
        </>}

        {hasFilters && (
          <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Clear all{activeFilterCount > 0 && (
              <span style={{
                background: 'var(--accent)', color: '#fff', borderRadius: 8,
                padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
              }}>{activeFilterCount}</span>
            )}
          </button>
        )}

        {filtered.length !== rows.length && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {filtered.length} of {rows.length}
          </span>
        )}
      </div>

      {/* Per-page toggle */}
      {view.paged && !isLoading && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span>Per page:</span>
          {[20, 50].map(n => (
            <button key={n} onClick={() => { setPageSize(n); setPage(1) }} style={{
              padding: '3px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
              border: '1px solid var(--border)',
              background: pageSize === n ? 'var(--accent)' : 'var(--surface-raised)',
              color: pageSize === n ? '#fff' : 'var(--text-secondary)',
              fontWeight: pageSize === n ? 600 : 400,
            }}>{n}</button>
          ))}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36, paddingRight: 0 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    checked={paginated.length > 0 && paginated.every(c => checkedIds.has(c.contact_id))}
                    ref={el => { if (el) { const pids = paginated.map(c => c.contact_id); el.indeterminate = pids.some(id => checkedIds.has(id)) && !pids.every(id => checkedIds.has(id)) } }}
                    onChange={toggleCheckAll}
                    title="Select all visible"
                  />
                </th>
                {cols.map(col => col.sort
                  ? <SortHeader key={col.header} col={col.sort} sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>{col.header}</SortHeader>
                  : <th key={col.header}>{col.header}</th>
                )}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={cols.length + 2}>
                  <div className="empty-state" style={{ padding: '40px 20px' }}>
                    <h3>{hasFilters ? view.empty.filtered : view.empty.none}</h3>
                    <p>{!hasFilters && view.empty.hint}</p>
                  </div>
                </td></tr>
              ) : (
                paginated.map(c => (
                  <tr
                    key={c.contact_id}
                    style={{
                      cursor: 'pointer',
                      background: checkedIds.has(c.contact_id) ? 'var(--accent-muted)' : undefined,
                    }}
                    onClick={() => setSelectedId(c.contact_id)}
                  >
                    <td style={{ width: 36, paddingRight: 0 }}>
                      <input
                        type="checkbox"
                        checked={checkedIds.has(c.contact_id)}
                        onChange={e => toggleCheck(c.contact_id, e)}
                        onClick={e => e.stopPropagation()}
                        style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                      />
                    </td>
                    {cols.map(col => <td key={col.header} style={col.style}>{col.cell(c, ctx)}</td>)}
                    <td style={{ width: 36, paddingRight: 8 }}>
                      <button
                        onClick={e => { e.stopPropagation(); setToDelete(c) }}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 4,
                          display: 'inline-flex', alignItems: 'center',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        title={`Delete ${view.noun.toLowerCase()}`}
                      ><TrashIcon /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination bar */}
      {view.paged && !isLoading && filtered.length > 0 && (
        <PaginationBar
          page={safePage}
          totalPages={totalPages}
          pageStart={pageStart}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPage={setPage}
        />
      )}

      {/* Create modal */}
      {createOpen && (
        <Modal title={view.createTitle} onClose={() => setCreate(false)}>
          <PersonForm
            defaultTypes={view.defaultTypes}
            firms={firms}
            allContacts={contacts}
            users={users}
            tagSuggestions={tagSuggestions ?? []}
            onSubmit={createMutation.mutateAsync}
            onCancel={() => setCreate(false)}
            submitting={createMutation.isPending}
            error={createMutation.error?.response?.data?.error}
          />
        </Modal>
      )}

      {/* Detail modal */}
      {selected && (
        <PersonModal
          person={selected}
          profileBase={profileBase}
          onClose={() => setSelectedId(null)}
          firms={firms}
          allContacts={contacts}
          users={users}
          tagSuggestions={tagSuggestions ?? []}
        />
      )}

      {/* Delete confirmation */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.name}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.contact_id)}
          deleting={deleteMutation.isPending}
        />
      )}

      {/* Merge modal */}
      {mergeOpen && checkedRecords.length >= 2 && (
        <MergeModal
          records={checkedRecords}
          entityType="contact"
          primaryId={mergePrimaryId}
          onPrimaryChange={setMergePrimaryId}
          onConfirm={() => mergeMutation.mutate({
            primary_id: mergePrimaryId,
            merge_ids:  checkedRecords.map(c => c.contact_id).filter(id => id !== mergePrimaryId),
          })}
          onCancel={() => { setMergeOpen(false); setMergeError(null) }}
          merging={mergeMutation.isPending}
          error={mergeError}
        />
      )}

      {/* Bulk-delete confirmation modal */}
      {bulkDeleteOpen && (
        <div
          onClick={e => e.target === e.currentTarget && !bulkDeleting && setBulkDeleteOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1200, padding: 16,
          }}
        >
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 10, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
          }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
              Delete {checkedIds.size} {plural(checkedIds.size)}?
            </h2>

            {/* Name list — up to 10 */}
            <ul style={{ margin: '0 0 14px', padding: '0 0 0 18px', fontSize: 13, color: 'var(--text-primary)' }}>
              {checkedRecords.slice(0, 10).map(c => (
                <li key={c.contact_id} style={{ marginBottom: 2 }}>{c.name}</li>
              ))}
              {checkedRecords.length > 10 && (
                <li style={{ color: 'var(--text-secondary)' }}>
                  …and {checkedRecords.length - 10} more
                </li>
              )}
            </ul>

            {/* Warning */}
            <div style={{
              background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 12,
              color: 'var(--danger-text)', marginBottom: 20, lineHeight: 1.5,
            }}>
              This action cannot be undone. Associated interactions and pipeline
              entries linked solely to these contacts may be affected.
            </div>

            {/* Partial-failure result */}
            {bulkDeleteResult && (
              <div style={{
                background: 'var(--danger-muted)', borderRadius: 6,
                padding: '8px 12px', fontSize: 12, color: 'var(--danger-text)', marginBottom: 14,
              }}>
                {bulkDeleteResult.succeeded} deleted, {bulkDeleteResult.failed} failed.
                The failed records may have dependent data — delete them individually.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={bulkDelete} disabled={bulkDeleting}>
                {bulkDeleting ? 'Deleting…' : `Delete ${checkedIds.size} ${plural(checkedIds.size)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
